import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {NgClass} from '@angular/common';
import {MatTooltip} from '@angular/material/tooltip';
import {CarouselComponent, CarouselModule, OwlOptions} from 'ngx-owl-carousel-o';
import {CarouselService} from 'ngx-owl-carousel-o/lib/services/carousel.service';
import {WINDOW} from '../../../universal/window/window.service';
import {RtElementResizedDirective} from '../../../rt-element-resized/rt-element-resized.directive';
import {DefineCarouselItemWidthPipe} from '../../../dashboard-shared/pipes/define-carousel-item-width.pipe';
import {DashboardStudyCarouselItemFields} from '../../types/dashboard-study.types';

/**
 * Keys identifiers for two different ways of key defining merged in the enum.
 */
enum KEYS_IDENTIFIERS {
  /** Right arrow code in the deprecated API. */
  NEXT_BUTTON_CODE = 39,

  /** Left arrow code in the deprecated API. */
  PREV_BUTTON_CODE = 37,

  /** Right arrow code in the new API. */
  NEXT_BUTTON_NAME = 'ArrowRight',

  /** Left arrow code in the new API. */
  PREV_BUTTON_NAME = 'ArrowLeft',
}

/**
 * Enum for the carousel selection direction.
 * Describes the position of the just selected carousel item relative to the current one.
 */
enum CarouselSelectionDirection {
  /** The selected item is placed after the current one. */
  Next = 1,

  /** The selected item is the same as the current one. */
  NotChanged = 0,

  /** The selected item is placed before the current one. */
  Previous = -1,
}

@Component({
  selector: 'app-dashboard-study-carousel',
  templateUrl: './dashboard-study-carousel.component.html',
  styleUrls: ['./dashboard-study-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RtElementResizedDirective, CarouselModule, MatTooltip, NgClass, DefineCarouselItemWidthPipe, DefineCarouselItemWidthPipe],
})
export class DashboardStudyCarouselComponent<T extends DashboardStudyCarouselItemFields> {
  @ViewChild('brainRegionsCarousel') brainRegionsCarousel: CarouselComponent;
  @ViewChildren('carouselItemElement') carouselItemElement: QueryList<ElementRef<HTMLElement>>;

  /** Contain a list of carousel items. */
  @Input() carouselItems: T[];

  /** Contain current slide id. */
  @Input() activeSlideId: T['id'];

  /** Notifies parent component once slide changed. */
  @Output() slideChanged = new EventEmitter<T>();

  /** Describes styles for carousel item stroke on desktop screens. */
  public readonly desktopStrokeStyle = `500 16px Montserrat, sans-serif`;

  /** Describes styles for carousel item stroke on mobile screens. */
  public readonly mobileStrokeStyle = `500 14px Montserrat, sans-serif`;

  public readonly activeSliceClass = 'carousel__item_active';

  /** Configs for owl carousel. */
  public readonly carouselOptions: OwlOptions = {
    items: 2,
    loop: true,
    mouseDrag: true,
    touchDrag: true,
    pullDrag: true,
    dots: false,
    navSpeed: 700,
    navText: ['', ''],
    nav: false,
    margin: 6,
    autoWidth: true,
    responsive: {
      0: {
        center: true,
      },
    },
  };

  constructor(@Inject(WINDOW) public window: Window) {}

  /**
   * Listen to keyup event; select next/prev slide on specific key press.
   */
  @HostListener('window:keyup', ['$event']) keyEvent(event: KeyboardEvent): void {
    // `event.keyCode` is deprecated, but `event.key` may not be supported in old browsers.
    // Try each option with higher priority for "event.key"
    let carouselShift = CarouselSelectionDirection.NotChanged;
    if (event.key !== undefined) {
      const keyName = event.key;
      if (keyName === KEYS_IDENTIFIERS.NEXT_BUTTON_NAME) {
        carouselShift = CarouselSelectionDirection.Next;
      } else if (keyName === KEYS_IDENTIFIERS.PREV_BUTTON_NAME) {
        carouselShift = CarouselSelectionDirection.Previous;
      }
    } else if (event.keyCode !== undefined) {
      const keyCode = event.keyCode;
      if (keyCode === KEYS_IDENTIFIERS.NEXT_BUTTON_CODE) {
        carouselShift = CarouselSelectionDirection.Next;
      } else if (keyCode === KEYS_IDENTIFIERS.PREV_BUTTON_CODE) {
        carouselShift = CarouselSelectionDirection.Previous;
      }
    }

    // Not defined or not loaded yet.
    if (!carouselShift || !this.activeSlideId || !this.carouselItems) {
      return;
    }

    // Find the nearest available slide and select it.
    const currentSlide = this.carouselItems.find(item => item.id === this.activeSlideId);
    const slideToSelect = this.findNearestAvailableSlide(carouselShift, currentSlide);
    this.selectSlide(slideToSelect);
    event.preventDefault();
  }

  /**
   * Trigger resize event on the carousel.
   */
  public resizeBrainRegionsCarousel(): void {
    const windowWidth = this.window.innerWidth;
    const anyCarousel = this.brainRegionsCarousel as any;

    if (anyCarousel?.carouselService) {
      const carouselService = anyCarousel.carouselService as CarouselService;
      carouselService.onResize(windowWidth);
    }
  }

  /**
   * Notify a parent element that the current slide changed.
   */
  public selectSlide(selectedSlide: T, shouldScroll = true, direction: CarouselSelectionDirection = CarouselSelectionDirection.Next): void {
    // Ignore selection if the slide doesn't exist.
    if (!selectedSlide) {
      return;
    }

    // If all slides are unavailable, do nothing.
    if (!this.carouselItems.filter(item => item.is_available)?.length) {
      return;
    }

    // Select the nearest available slide if the current one is unavailable.
    if (!selectedSlide.is_available) {
      selectedSlide = this.findNearestAvailableSlide(direction, selectedSlide);
    }

    // Update the current slide.
    this.activeSlideId = selectedSlide.id;
    if (shouldScroll && this.brainRegionsCarousel) {
      this.brainRegionsCarousel.to(String(selectedSlide.id));
    }

    this.slideChanged.emit(selectedSlide);
  }

  /**
   * Handle the click on the slide.
   * Select the correct slide based on its availability.
   * If the selected slide is unavailable, select the next or the previous available slide based on the click position.
   *
   * @param event - click event.
   * @param carouselItem - clicked carousel item.
   */
  public slideClicked(event: MouseEvent, carouselItem: T) {
    // If the selected item is unavailable, we want to select the next or the previous available item based on the click position.
    // To determine the direction, we need to compare the click position with the current item's position.
    if (!carouselItem?.is_available) {
      const elementToSelect = event?.target as HTMLElement;
      const elementToSelectXPosition = elementToSelect?.getBoundingClientRect?.()?.x;
      const windowWidth = this.window?.innerWidth;

      // Unable to get the element's position.
      if (typeof elementToSelectXPosition !== 'number') {
        return;
      }

      // Carousel items are not loaded yet.
      if (!this.carouselItemElement?.length) {
        return;
      }

      // Unable to get the window width (it seems like the code is running in the server environment).
      if (!windowWidth) {
        return;
      }

      for (const item of this.carouselItemElement) {
        if (item?.nativeElement?.classList?.contains?.(this.activeSliceClass)) {
          const currentItemXPosition = item.nativeElement?.getBoundingClientRect?.()?.x;

          // As owl carousel creates a few clones of each region for the infinite loop, we need to find the item in the visible area.
          if (currentItemXPosition > 0 && currentItemXPosition < windowWidth) {
            // Select the next or the previous item depending on the click position.
            const direction =
              elementToSelectXPosition > currentItemXPosition ? CarouselSelectionDirection.Next : CarouselSelectionDirection.Previous;
            this.selectSlide(carouselItem, true, direction);
            return;
          }
        }
      }
    }

    // Select the clicked item as it is available.
    this.selectSlide(carouselItem);
  }

  /**
   * Find the nearest available slide in the specified direction.
   */
  public findNearestAvailableSlide(direction: CarouselSelectionDirection, fromSlide: T): T {
    // Filter out unavailable items as they cannot be selected; keep the relative item as it will be used for the comparison.
    const availableCarouselItems = this.carouselItems.filter(item => item.is_available || item.id === fromSlide.id);

    // Get the indices of the provided and the next items.
    const fromSlideItemIdx = availableCarouselItems.findIndex(item => item.id === fromSlide.id);
    const itemToSelectIdx = fromSlideItemIdx + (direction === CarouselSelectionDirection.Next ? 1 : -1);

    // Check boundaries and adjust the index if needed.
    const limit = availableCarouselItems.length;
    let safeItemToSelectIdx = itemToSelectIdx;
    if (safeItemToSelectIdx < 0) {
      safeItemToSelectIdx = limit - 1;
    } else if (safeItemToSelectIdx === limit) {
      safeItemToSelectIdx = 0;
    }

    // Return the item to select.
    return availableCarouselItems[safeItemToSelectIdx];
  }
}
